/*
  SSL Pinning helper.
  - If native SSL pinning is available (react-native-ssl-pinning), use it.
  - In production: Enforce SSL pinning, throw error if not available or fails.
  - In development: Allow fallback to standard fetch with warning.
  - Configure pins via env or config.
  
  🔒 SECURITY: Production mode enforces SSL pinning for MITM protection
*/

import { Platform } from 'react-native';
import config from '../config';
import logger from '../utils/logger';

type PinnedFetchOptions = RequestInit & {
    timeoutMs?: number;
};

// SHA256 base64 pins list; provide via config or env
const CERT_PINS: string[] = (config.SSL_PINS || []).filter(Boolean);

// Check if we're in production
const isProduction = process.env.NODE_ENV === 'production' || !__DEV__;

// Check if we're on web - SSL pinning is not available/needed on web (browser handles it)
const isWeb = Platform.OS === 'web';

export async function pinnedFetch(input: string, init?: PinnedFetchOptions): Promise<Response> {
    // 🌐 WEB: Use standard fetch (browser handles SSL/TLS automatically)
    if (isWeb) {
        logger.debug('Using standard fetch on web platform', {
            url: input,
            platform: 'web'
        });
        return fetch(input, init);
    }

    // 🔒 SECURITY CHECK 1: No pins configured (mobile only)
    if (!CERT_PINS.length) {
        const errorMessage = 'SSL certificate pins not configured';
        
        if (isProduction) {
            // ❌ PRODUCTION: Throw error - cannot proceed without SSL pinning
            logger.error(errorMessage, {
                url: input,
                environment: 'production',
                action: 'blocking_request'
            });
            throw new Error(`${errorMessage}. Application security requirement not met.`);
        }
        
        // ⚠️ DEVELOPMENT: Warn and allow fallback
        logger.warn(`${errorMessage}. Using standard fetch (development mode only)`, {
            url: input,
            environment: 'development'
        });
        return fetch(input, init);
    }

    // Try to use native ssl pinning package
    try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { fetch: rnFetch } = require('react-native-ssl-pinning');
        const timeout = init?.timeoutMs ?? 20000;
        const method = (init?.method || 'GET').toUpperCase();
        const headers = init?.headers as Record<string, string> | undefined;
        const body = init?.body as string | undefined;

        logger.debug('Executing SSL pinned request', {
            url: input,
            method,
            pinsConfigured: CERT_PINS.length
        });

        const resp = await rnFetch(input, {
            method,
            headers,
            body,
            timeoutInterval: timeout,
            sslPinning: {
                certs: CERT_PINS, // expects cert names or hashes depending on library config
            },
        });

        // Adapt to standard Response-like
        const responseBody = resp.bodyString ?? '';
        const status = resp.status ?? 0;
        const responseHeaders = new Headers();
        if (resp.headers) {
            Object.keys(resp.headers).forEach((k) => {
                const v = resp.headers[k];
                if (Array.isArray(v)) v.forEach((vv) => responseHeaders.append(k, String(vv)));
                else if (v !== undefined && v !== null) responseHeaders.set(k, String(v));
            });
        }
        
        logger.debug('SSL pinned request successful', {
            url: input,
            status,
            responseSize: responseBody.length
        });
        
        return new Response(responseBody, { status, headers: responseHeaders });
    } catch (e: unknown) {
        // 🔒 SECURITY CHECK 2: SSL pinning validation failed
        const errorMessage = 'SSL certificate pinning validation failed';
        
        logger.error(errorMessage, {
            url: input,
            error: e?.message || String(e),
            pinsConfigured: CERT_PINS.length,
            environment: isProduction ? 'production' : 'development'
        });

        if (isProduction) {
            // ❌ PRODUCTION: Throw error - possible MITM attack
            throw new Error(
                `${errorMessage}. This may indicate a man-in-the-middle attack or network security issue. ` +
                `Request blocked for security. Error: ${e?.message || String(e)}`
            );
        }
        
        // ⚠️ DEVELOPMENT: Warn and allow fallback
        logger.warn(
            `${errorMessage}. Falling back to standard fetch (development mode only). ` +
            `In production, this request would be blocked.`,
            { url: input, error: e?.message || String(e) }
        );
        
        return fetch(input, init);
    }
}




